import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {distinctUntilChanged, map} from 'rxjs/operators';
import {WizardEnums, WizardInterfaces} from '../../wizard.namespace';
import {ObjectsProvider} from '@providers/objects/objects.provider';
import {MethodsProvider} from '@providers/methods/methods.provider';

@Injectable()
export class WizardFormEventsService {
    private _isRk$ = new BehaviorSubject<boolean>(false);
    private _attestationChange$ = new BehaviorSubject<WizardInterfaces.AttestationValue[]>([]);
    private _staffIdsChange$ = new BehaviorSubject<number[]>([]);
    private _hasNoModerated$ = new BehaviorSubject<boolean>(false);
    private _laboratoryAttestationType$ = new BehaviorSubject<WizardEnums.LaboratoryAttestationTypes>(null);


    private _laboratoryLeaderId$ = new BehaviorSubject<number>(null);
    private _organizationOwnerName$ = new BehaviorSubject<string>(null);
    private _organizationOwnerJob$ = new BehaviorSubject<string>(null);
    private _organizationTitle$ = new BehaviorSubject<string>(null);
    private _organizationBusinessStructureId$ = new BehaviorSubject<number>(null);
    private _organizationInn$ = new BehaviorSubject<string>(null);


    constructor(
        private objectsProvider: ObjectsProvider,
        private methodsProvider: MethodsProvider,
    ) {

    }


    /**
     * Выбрана или нет аттестация с РК.
     * true - если добавили РК в области или выбрали «Аттестовать с РК».
     * false - если не выбрано РК и не выбрано «Аттестовать с РК»
     */
    public isRk$ = this._isRk$.asObservable().pipe(
        map(v => Boolean(v)),
        distinctUntilChanged(),
    );

    /**
     * Изменилось значение области аттестации
     */
    public attestationChange$ = this._attestationChange$.asObservable().pipe(
        map((value) => value.filter(v => Boolean(v.key && v.objects && v.objects.length))),
        distinctUntilChanged((old, current) => JSON.stringify(old) === JSON.stringify(current)),
        map((value: WizardInterfaces.AttestationValue[]) => {
            /**
             * Добавляем
             */
            return value.map(v => {
                const method = this.methodsProvider.getByShortTitle(v.key);
                return {
                    ...v,
                    methodId: method ? method.id : null,
                    objects: v.objects.map(o => {
                        const object = this.objectsProvider.getById(o.id);
                        return {
                           ...o,
                           realNumbers: object ? ObjectsProvider.getRootAndChildsNumbers(object) : []
                        };
                    })
                };
            });
        })
    );

    /**
     * Изменился список ids выбранного персонала лаборатории
     */
    public staffIdsChange$ = this._staffIdsChange$.asObservable().pipe(
        map(v => v.map(id => +id)),
        distinctUntilChanged((old, current) => JSON.stringify(old) === JSON.stringify(current)),
    );

    /**
     * Имеется ли непромодерированное оборудование
     */
    public hasNoModerated$ = this._hasNoModerated$.asObservable().pipe(
        distinctUntilChanged(),
    );

    /**
     * Именение типа аттестации лаборатории
     */
    public laboratoryAttestationType$ = this._laboratoryAttestationType$.asObservable().pipe(
        distinctUntilChanged(),
    );

    /**
     * Изменение id специалиста, являющегося реководителем лаборатории
     */
    public laboratoryLeaderId$ = this._laboratoryLeaderId$.asObservable().pipe(
        distinctUntilChanged(),
    );

    /**
     * Изменение имени руководителя организации
     */
    public organizationOwnerName$ = this._organizationOwnerName$.asObservable().pipe(
        distinctUntilChanged(),
    );

    /**
     * Изменение должности руководителя организации
     */
    public organizationOwnerJob$ = this._organizationOwnerJob$.asObservable().pipe(
        distinctUntilChanged(),
    );

    /**
     * Изменение наименования организации
     */
    public organizationTitle$ = this._organizationTitle$.asObservable().pipe(
        distinctUntilChanged(),
    );

    /**
     * Изменение типа формы собственности (ООО, ОАО, ЗАО, ИП и т.п.)
     */
    public organizationBusinessStructureId$ = this._organizationBusinessStructureId$.asObservable().pipe(
        distinctUntilChanged(),
    );

    /**
     * Изменение значения ИНН организации
     */
    public organizationInn$ = this._organizationInn$.asObservable().pipe(
        distinctUntilChanged(),
    );

    setIsRk(value: boolean) {
        this._isRk$.next(value);
    }

    setAttestation(value: WizardInterfaces.AttestationValue[]) {
        this._attestationChange$.next(value);
    }

    setStaffIds(ids: number[]) {
        this._staffIdsChange$.next(ids);
    }

    setNotModerated(value: boolean) {
        this._hasNoModerated$.next(value);
    }

    setLaboratoryAttestationType(type: WizardEnums.LaboratoryAttestationTypes) {
        this._laboratoryAttestationType$.next(type);
    }

    setLaboratoryLeaderId(id: number) {
        this._laboratoryLeaderId$.next(id);
    }

    setOrganizationOwnerName(name: string) {
        this._organizationOwnerName$.next(name);
    }

    setOrganizationOwnerJob(job: string) {
        this._organizationOwnerJob$.next(job);
    }

    setOrganizationTitle(title: string) {
        this._organizationTitle$.next(title);
    }

    setOrganizationBusinessStructureId(id: number) {
        this._organizationBusinessStructureId$.next(id);
    }

    setOrganizationInn(inn: string) {
        this._organizationInn$.next(inn);
    }
}
